<script lang="ts">
  /**
   * A tiny margin annotation: "Aug 20".
   *
   * Rendered beside the first task of each logical day rather than as a divider
   * between rows. Two consequences that are the point of the design
   * (ARCHITECTURE.md §4, guarantee 5):
   *
   * * It never consumes a functional task slot.
   * * It is anchored to a task's identity, not to a line number, so it cannot
   *   drift away from its task when rows are repositioned on re-sync.
   *
   * The full date is stored; the year is appended only when the marker falls in
   * a different calendar year than the viewer's, so a normal page reads as
   * "Aug 20" rather than "Aug 20, 2024".
   */
  interface Props {
    /** `YYYY-MM-DD`, from the task's server-resolved `logical_day`. */
    day: string;
    /** The year the viewer is thinking in; the current one by default. */
    inYear?: number;
    /**
     * Archived context (browse/graveyard): the year is ALWAYS appended. The
     * `inYear` comparison alone cannot force a same-year marker to show its
     * year (ARCHITECTURE.md §4, guarantee 5).
     */
    forceYear?: boolean;
  }

  let { day, inYear = new Date().getFullYear(), forceYear = false }: Props = $props();

  const MONTHS = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
  ];

  /** `{ label, showYear }` for one logical day. */
  function format(day: string): { label: string; showYear: boolean } {
    const [year, month] = day.split('-');
    const monthIndex = Number(month) - 1;
    const label = MONTHS[monthIndex] ?? '';
    const parsedYear = Number(year);
    return { label: `${label} ${Number(day.slice(8, 10))}`, showYear: parsedYear !== inYear || forceYear };
  }

  const shown = $derived(format(day));
</script>

<!--
  aria-hidden: a date annotation is decoration. A screen-reader user gets the
  date from the task it belongs to, not from a floating label.
-->
<span class="date-marker" aria-hidden="true" title={day}>
  {shown.label}{#if shown.showYear}&nbsp;{day.slice(0, 4)}{/if}
</span>